import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, apiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Loader2, FileText } from "lucide-react";

export default function Consent() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    const [c, h] = await Promise.all([api.get("/consent/current"), api.get("/consent/history")]);
    setData(c.data);
    setHistory(h.data.history);
  };
  useEffect(() => { load(); }, []);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/consent/accept");
      await refreshUser();
      await load();
      toast.success("Consent recorded");
      navigate("/patient/record");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.post("/consent/withdraw");
      await refreshUser();
      await load();
      toast("Consent withdrawn — screening steps are now blocked.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const status = user.consent_status;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="eyebrow">Step · Consent</div>
      <h1 className="font-head text-3xl font-bold text-ink mt-1">Informed consent</h1>
      <p className="text-ink-soft mt-2">Version {data.version} — required before any voice or genetic step.</p>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2 text-brand mb-4">
          <FileText className="h-5 w-5" />
          <span className="font-head font-semibold">ManoGene Pilot Consent</span>
        </div>
        <p className="text-[15px] leading-relaxed text-ink-soft whitespace-pre-line" data-testid="consent-text">
          {data.text}
        </p>
      </div>

      {status === "accepted" ? (
        <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-green-800 flex-1" data-testid="consent-accepted-banner">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-sm font-medium">Consent accepted (v{user.consent_version}). You may proceed to voice screening.</span>
          </div>
          <button onClick={withdraw} disabled={busy} data-testid="consent-withdraw-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-red-300 text-red-600 px-4 py-3 text-sm font-semibold hover:bg-red-50 transition-colors">
            <ShieldOff className="h-4 w-4" /> Withdraw consent
          </button>
        </div>
      ) : (
        <div className="mt-6">
          {status === "withdrawn" && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              You previously withdrew consent. Re-accept below to resume screening. Prior records are retained for audit.
            </div>
          )}
          <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 cursor-pointer">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)}
              data-testid="consent-checkbox" className="mt-1 h-4 w-4 accent-[#1E3A8A]" />
            <span className="text-sm text-ink-soft">
              I have read and understood the above. I give my informed, voluntary consent to participate in the ManoGene screening pilot.
            </span>
          </label>
          <button onClick={accept} disabled={!checked || busy} data-testid="consent-accept-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-6 py-3 font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Accept & continue
          </button>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-10">
          <div className="eyebrow mb-2">Consent audit trail</div>
          <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className={`font-medium ${h.action === "accepted" ? "text-green-700" : "text-red-600"}`}>
                  {h.action === "accepted" ? "Accepted" : "Withdrawn"} · v{h.version}
                </span>
                <span className="mono text-xs text-ink-muted">{new Date(h.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
